const SECRET = "q-secret-" + Math.random();

const _NULL = "null";
const _BOOL = "bool";
const _VAL = "val";
const _REF = "ref";
const _DOT = ".";
const _AT = "at";
const _NOT = "!";
const _LT = "<";
const _LTE = "<=";
const _GT = ">";
const _GTE = ">=";
const _EQ = "==";
const _NEQ = "!=";
const _AND = "&&";
const _OR = "||";
const _CALL = "call";
const _ANY = "any";

const T_BOOLEAN = "BOOLEAN";
const T_INTEGER = "INTEGER";
const T_DECIMAL = "DECIMAL";
const T_STRING = "STRING";
const T_DATE = "DATE";
const T_DATETIME = "DATETIME";
const T_TIME = "TIME";
const T_UNBOUND = "UNBOUND";

const isBooleanType = type =>
    type === T_BOOLEAN || type === T_UNBOUND;

const isStringType = type =>
    type === T_STRING || type === T_UNBOUND;

const isTimeType = type =>
    type === T_UNBOUND || type === T_TIME;

const isTemporalType = type =>
    isTimeType(type) || type === T_DATE || type === T_DATETIME;

const dot = (base, name) =>
    base == null ? null
        : typeof base.get === "function"
            ? base.get(name)
            : base[name];

const at = (base, index) =>
    base == null ? null
        : typeof base.at === "function"
            ? base.at(index)
            : base[index];

const toLowerCase = q =>
    q.state._ === _VAL
        ? Q.value(q.state.value.toLowerCase(), T_STRING)
        : q.call("toLowerCase", T_STRING);

const getCompatibleType = (left, right) => {
    if (left === Q.null()) {
        return right.getType();
    }
    if (right === Q.null()) {
        return left.getType();
    }
    const leftUnbound = left.getType() === T_UNBOUND;
    const rightUnbound = right.getType() === T_UNBOUND;
    if (leftUnbound && rightUnbound) {
        throw new TypeError(
            "Comparison ops require type information on at least one side.");
    }
    if (!leftUnbound && !rightUnbound && left.getType() !== right.getType()) {
        throw new TypeError("Comparison ops only operate on like types.");
    }
    return leftUnbound ? right.getType() : left.getType();
};

const formatValue = (val, type) => {
    if (val == null) {
        return "null";
    }
    switch (type) {
        case T_STRING:
        case T_INTEGER:
        case T_DECIMAL: return val.toString();
        case T_DATE: return `${val.year()}/${val.month() + 1}/${val.date()}`;
        case T_DATETIME: return `${val.year()}/${val.month() + 1}/${val.date()}T${val.hours()}:${val.minutes()}:${val.seconds()}`;
        case T_TIME: return `T${val.hours()}:${val.minutes()}:${val.seconds()}`;
        default: throw new TypeError(`Unknown '${type}' value`);
    }
};

const zeroPad = (n, l=2) => {
    let s = n.toString();
    while (s.length < l) {
        s = "0" + s;
    }
    return s;
};

const formatQSValue = (val, type) => {
    if (val == null) {
        return "null";
    }
    switch (type) {
        case T_STRING: return encodeURIComponent(val);
        case T_INTEGER:
        case T_DECIMAL: return val.toString();
        case T_DATE: return `${zeroPad(val.year(), 4)}-${zeroPad(val.month() + 1)}-${zeroPad(val.date())}`;
        case T_DATETIME: return `${zeroPad(val.year(), 4)}-${zeroPad(val.month() + 1)}-${zeroPad(val.date())}T${zeroPad(val.hours())}:${zeroPad(val.minutes())}:${zeroPad(val.seconds())}`;
        case T_TIME: return `${zeroPad(val.hours())}:${zeroPad(val.minutes())}:${zeroPad(val.seconds())}`;
        default: throw new TypeError(`Unknown '${type}' value`);
    }
};

class Q {

    constructor(secret, state) {
        if (secret !== SECRET) {
            throw new Error("You cannot construct Q instances; use a factory method");
        }
        this.state = state;
        this.test = this.test.bind(this);
        const comparisonMethodFactory = _ => right => {
            const type = getCompatibleType(this, right);
            if (isTimeType(type)) {
                throw new Error("Cannot compare times except for equality");
            }
            return new Q(SECRET, {_, left: this, right});
        };
        this.lt = comparisonMethodFactory(_LT);
        this.lte = comparisonMethodFactory(_LTE);
        this.gt = comparisonMethodFactory(_GT);
        this.gte = comparisonMethodFactory(_GTE);
    }

    and(q) {
        if (!isBooleanType(this.getType()) || !isBooleanType(q.getType())) {
            throw new TypeError("Cannot .and non-boolean expressions");
        }
        if (this.state._ === _BOOL) {
            return this.state.value ? q : this;
        }
        if (q.state._ === _BOOL) {
            return q.state.value ? this : q;
        }
        return new Q(SECRET, {_: _AND, left: this, right: q});
    }

    or(q) {
        if (!isBooleanType(this.getType()) || !isBooleanType(q.getType())) {
            throw new TypeError("Cannot .or non-boolean expressions");
        }
        if (this.state._ === _BOOL) {
            return this.state.value ? this : q;
        }
        if (q.state._ === _BOOL) {
            return q.state.value ? q : this;
        }
        return new Q(SECRET, {_: _OR, left: this, right: q});
    }

    at(index) {
        const type = this.getType();
        if (type !== T_UNBOUND) {
            throw new TypeError(`Cannot use 'at' on ${type} expressions, only refs`);
        }
        return new Q(SECRET, {_: _AT, base: this, index});
    }

    dot(name) {
        const type = this.getType();
        if (type !== T_UNBOUND) {
            throw new TypeError(`Cannot use 'dot' on ${type} expressions, only refs`);
        }
        return new Q(SECRET, {_: _DOT, base: this, name});
    }

    any(test) {
        const type = this.getType();
        if (type !== T_UNBOUND) {
            throw new TypeError(`Cannot use 'any' on ${type} expressions, only refs`);
        }
        if (!isBooleanType(test.getType())) {
            throw new TypeError("Cannot .any a non-boolean test");
        }
        return new Q(SECRET, {_: _ANY, delegate: this, test});
    }

    eq(right) {
        getCompatibleType(this, right); // for fail-fast behaviour
        return new Q(SECRET, {_: _EQ, left: this, right});
    }

    neq(right) {
        getCompatibleType(this, right); // for fail-fast behaviour
        return new Q(SECRET, {_: _NEQ, left: this, right});
    }

    isNull() {
        if (this.state._ === _VAL) {
            return this.state.value == null ? Q.true() : Q.false();
        }
        return this.eq(Q.null());
    }

    isNotNull() {
        if (this.state._ === _VAL) {
            return this.state.value == null ? Q.false() : Q.true();
        }
        return this.neq(Q.null());
    }

    contains(right) {
        if (right == null) {
            return Q.true();
        }
        if (! isStringType(this.getType()) || ! isStringType(right.getType())) {
            throw new TypeError(`Cannot .contains non-string expressions`);
        }
        return this.isNotNull().and(right.isNull().or(
            toLowerCase(this).call("contains", T_BOOLEAN, [
                toLowerCase(right)
            ])
        ));
    }

    call(method, type, args = []) {
        return new Q(SECRET, {_: _CALL, delegate: this, method, args, type});
    }

    negate() {
        if (!isBooleanType(this.getType())) {
            throw new TypeError("Cannot .negate a non-boolean");
        }
        if (this.state._ === _BOOL) {
            return this.state.value ? Q.false() : Q.true();
        }
        if (this.state._ === _EQ) {
            return this.state.left.neq(this.state.right);
        }
        if (this.state._ === _NEQ) {
            return this.state.left.eq(this.state.right);
        }
        return new Q(SECRET, {_: _NOT, base: this});
    }

    // like SQL's BETWEEN, both endpoints are included
    between(start, end) {
        let e = Q.true();
        if (start != null) {
            e = e.and(this.gte(start));
        }
        if (end != null) {
            e = e.and(this.lte(end));
        }
        return e;
    }

    getType() {
        const s = this.state;
        switch (s._) {
            case _NULL: return T_UNBOUND;
            case _BOOL: return T_BOOLEAN;
            case _VAL: return s.type;
            case _REF:
            case _DOT:
            case _AT: return s.type || T_UNBOUND;
            case _NOT:
            case _EQ:
            case _NEQ:
            case _LT:
            case _LTE:
            case _GT:
            case _GTE:
            case _AND:
            case _OR: return T_BOOLEAN;
            case _CALL: return s.type;
            case _ANY: return T_BOOLEAN;
            default: throw new TypeError("Unknown '" + s._ + "' type");
        }
    }

    toString() {
        const s = this.state;
        switch (s._) {
            case _NULL: return s._;
            case _BOOL: return "" + s.value;
            case _VAL: return "(" + formatValue(s.value, s.type) + " " + s.type + ")";
            case _REF: return s.type == null ? s.name : ("(" + s.name + " " + s.type + ")");
            case _DOT: return "(" + s._ + " " + s.base.toString() + " " + s.name + ")";
            case _AT: return "(" + s._ + " " + s.base.toString() + " " + s.index + ")";
            case _NOT: return "(" + s._ + " " + s.base.toString() + ")";
            case _EQ:
            case _NEQ:
            case _LT:
            case _LTE:
            case _GT:
            case _GTE:
            case _AND:
            case _OR: return "(" + s._ + " " + s.left.toString() + " " + s.right.toString() + ")";
            case _CALL: return "(" + s._ + " " + s.delegate.toString() + " " + s.method + s.args.map(it =>
                " " + it.toString()).join("") + ")";
            case _ANY: return "(" + s._ + " " + s.delegate.toString() + " " + s.test.toString() + ")";
            default: throw new TypeError("Unknown '" + s._ + "' type");
        }
    }

    toObjectFilter() {
        console.log("Q.toObjectFilter is deprecated; use .test instead");
        return this.test;
    }

    toModelFilter() {
        console.log("Q.toModelFilter is deprecated; use .test instead");
        return this.test;
    }

    toFilter() {
        if (this.getType() !== T_BOOLEAN) {
            console.log("a non-boolean filter function?!");
        }
        console.log("Q.toFilter is deprecated; use .test instead");
        return this.test;
    }

    test(record) {
        const s = this.state;
        let delegate;
        switch (s._) {
            case _NULL: return null;
            case _BOOL: return s.value;
            case _VAL: return s.value;
            case _REF: return dot(record, s.name);
            case _DOT: return dot(s.base.test(record), s.name);
            case _AT:return at(s.base.test(record), s.index);

            case _NOT: return ! s.base.test(record);

            case _EQ: // noinspection EqualityComparisonWithCoercionJS
                return s.left.test(record) == s.right.test(record);
            case _NEQ: // noinspection EqualityComparisonWithCoercionJS
                return s.left.test(record) != s.right.test(record);
            case _LT: return s.left.test(record) < s.right.test(record);
            case _LTE: return s.left.test(record) <= s.right.test(record);
            case _GT: return s.left.test(record) > s.right.test(record);
            case _GTE: return s.left.test(record) >= s.right.test(record);
            case _AND: return s.left.test(record) && s.right.test(record);
            case _OR: return s.left.test(record) || s.right.test(record);

            case _CALL:
                delegate = s.delegate.test(record);
                return delegate[s.method].apply(delegate, s.args.map(it => it.test(record)));
            case _ANY:
                delegate = s.delegate.test(record);
                return delegate == null ? false : delegate.find(it => s.test.test({it})) != null;
            default: throw new TypeError("Unknown '" + s._ + "' type");
        }
    }

    toQS() {
        const s = this.state;
        switch (s._) {
            case _NULL: return s._;
            case _BOOL: return "" + s.value;
            case _VAL: return formatQSValue(s.value, s.type);
            case _REF: return encodeURIComponent(s.name);
            case _EQ:
                const l_ = s.left.state._;
                if (l_ !== _REF) {
                    throw new Error(`When creating a querying, the LHS of EQ must be a ref ('${l_}' is unsupported)`);
                }
                const r_ = s.right.state._;
                if (r_ !== _NULL && r_ !== _BOOL && r_ !== _VAL) {
                    throw new Error(`When creating a querying, the RHS of EQ must be a value type ('${r_}' is unsupported)`);
                }
                return s.left.toQS() + "=" + s.right.toQS();
            case _AND: return s.left.toQS() + "&" + s.right.toQS();
            case _DOT:
            case _AT:
            case _NOT:
            case _NEQ:
            case _LT:
            case _LTE:
            case _GT:
            case _GTE:
            case _OR:
            case _CALL:
            case _ANY: throw new Error(`'${s._}' nodes are unsupported when creating a query string`);
            default: throw new TypeError("Unknown '" + s._ + "' type");
        }
    }

    toAS() {
        const s = this.state;
        switch (s._) {
            case _NULL: return "null";
            case _BOOL: return s.value ? "true" : "false";
            case _VAL:
                if (s.value == null) {
                    return "null";
                }
                switch (s.type) {
                    case T_INTEGER:
                    case T_DECIMAL: return s.value.toString();
                    case T_DATE: return `createDate(${s.value.year()},${s.value.month() + 1},${s.value.date()})`;
                    case T_DATETIME: return `createDateTime(${s.value.year()},${s.value.month() + 1},${s.value.date()},${s.value.hours()},${s.value.minutes()},${s.value.seconds()})`;
                    case T_TIME: return `createTime(${s.value.hours()},${s.value.minutes()},${s.value.seconds()})`;
                    case T_STRING:
                        if (s.value.indexOf(`"`) >= 0) {
                            throw new TypeError("Strings can't contain double quotes in ArmusScript");
                        }
                        return `"${s.value}"`;
                    default: throw new TypeError(`Unknown '${s.type}' value`);
                }
            case _REF: return s.name;
            case _DOT: return s.base.toAS() + "." + s.name;
            case _AT: return s.base.toAS() + "[" + s.index + "]";

            case _NOT: return "!(" + s.base.toAS() + ")";

            case _EQ: return s.left.toAS() + "==" + s.right.toAS();
            case _NEQ: return s.left.toAS() + "!=" + s.right.toAS();
            case _LT:
                return isTemporalType(getCompatibleType(s.left, s.right))
                    ? "isBefore(" + s.left.toAS() + "," + s.right.toAS() + ")"
                    : s.left.toAS() + "<" + s.right.toAS();
            case _LTE:
                return isTemporalType(getCompatibleType(s.left, s.right))
                    ? "!(isAfter(" + s.left.toAS() + "," + s.right.toAS() + "))"
                    : s.left.toAS() + "<=" + s.right.toAS();
            case _GT:
                return isTemporalType(getCompatibleType(s.left, s.right))
                    ? "isAfter(" + s.left.toAS() + "," + s.right.toAS() + ")"
                    : s.left.toAS() + ">" + s.right.toAS();
            case _GTE:
                return isTemporalType(getCompatibleType(s.left, s.right))
                    ? "!(isBefore(" + s.left.toAS() + "," + s.right.toAS() + "))"
                    : s.left.toAS() + ">=" + s.right.toAS();
            case _AND: return "(" + s.left.toAS() + "&&" + s.right.toAS() + ")";
            case _OR: return "(" + s.left.toAS() + "||" + s.right.toAS() + ")";

            case _CALL: return s.delegate.toAS() + "." + s.method + "(" + s.args.map(it => it.toAS()).join(",") + ")";
            case _ANY: return s.delegate.toAS() + ".any({" + s.test.toAS() + "})";
            default: throw new TypeError(`Unknown '${s._}' type`);
        }
    }

}

// literals
Q.TRUE = new Q(SECRET, {_: _BOOL, value: true});
Q.FALSE = new Q(SECRET, {_: _BOOL, value: false});
Q.NULL = new Q(SECRET, {_: _NULL});

// static factory functions (for literals)
Q.true = () => Q.TRUE;
Q.false = () => Q.FALSE;
Q.null = () => Q.NULL;

Q.ref = (name, type) =>
    new Q(SECRET, {_: _REF, name, type});

Q.value = (value, type) => {
    if (type == null) {
        throw new TypeError("Q.value must be passed a non-null type");
    }
    if (type === T_STRING && value != null && value.indexOf(`"`) >= 0) {
        console.warn("Q.value was passed a string containing a double quote. This will work for filtering, but not AS conversion.", value);
    }
    if (type === T_BOOLEAN) {
        if (value === true) {
            return Q.true();
        } else if (value === false) {
            return Q.false();
        }
    }
    return new Q(SECRET, {_: _VAL, value, type});
};

// obsolete factories left to throw helpful messages
Q.all = (...exprs) => {
    throw new Error("Use 'Q.conjunction', not 'Q.all'");
};
Q.any = (...exprs) => {
    throw new Error("Use 'Q.disjunction', not 'Q.any'");
};
Q.not = value => {
    throw new Error("Use 'q.negate', not 'Q.not'");
};
Q.equal = (left, right) => {
    throw new Error("Use 'q.eq', not 'Q.equal'");
};
Q.notEqual = (left, right) => {
    throw new Error("Use 'q.neq', not 'Q.notQqual'");
};

// utility functions
Q.conjunction = (...exprs) => {
    if (exprs.length === 1 && exprs[0] instanceof Array) {
        exprs = exprs[0];
    }
    const l = exprs.length;
    if (l === 0) {
        return Q.true();
    } else if (l === 1) {
        if (!isBooleanType(exprs[0].getType())) {
            throw new TypeError("Cannot .and non-boolean expressions");
        }
        return exprs[0];
    } else {
        let q = exprs[0];
        for (let i = 1; i < l; i++) {
            q = q.and(exprs[i]);
        }
        return q;
    }
};

Q.disjunction = (...exprs) => {
    if (exprs.length === 1 && exprs[0] instanceof Array) {
        exprs = exprs[0];
    }
    const l = exprs.length;
    if (l === 0) {
        return Q.false();
    } else if (l === 1) {
        if (!isBooleanType(exprs[0].getType())) {
            throw new TypeError("Cannot .or non-boolean expressions");
        }
        return exprs[0];
    } else {
        let q = exprs[0];
        for (let i = 1; i < l; i++) {
            q = q.or(exprs[i]);
        }
        return q;
    }
};

export default Q;



// WEBPACK FOOTER //
// ./app/lib/Q.js